import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import type { IconName } from '#/components/Icon/IconName';
import { useIsomorphicEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  userDashboardAccountSettingsPath,
  userDashboardApplicationHistoryPath,
  userDashboardNotificationPreferencesPath,
  userDashboardProfessionalExperiencePath,
  userDashboardProfilePath,
  userDashboardSavedItemsPath,
} from '#/modules/userDashboard/routing/userDashboard.paths';
import { hasResumeDb } from '#/utils/resumeDatabase';

export type MyAccountRowType =
  | 'saved-items'
  | 'profile'
  | 'professional-experience'
  | 'notification-preferences'
  | 'application-history'
  | 'account-settings';

export type MyAccountRow = {
  type: MyAccountRowType;
  href: string;
  icon: IconName;
  // Desktop's values, so all account menus report into the same analytics buckets
  linkType: string;
  label: string;
};

// Each menu orders the rows its own way, gated rows are left out
export function useMyAccountRows(
  user: ApiUser,
  order: MyAccountRowType[],
): MyAccountRow[] {
  const { i18n, locale } = useI18n();
  const { isomorphicEnvironment } = useIsomorphicEnvironment();

  const rowsByType: Record<MyAccountRowType, MyAccountRow | null> = {
    'saved-items': {
      type: 'saved-items',
      href: userDashboardSavedItemsPath.with({}),
      icon: 'bookmark',
      linkType: 'My Saved Items',
      label: i18n('My Saved Items'),
    },
    profile: user.hasIdealistProfileFeature
      ? {
          type: 'profile',
          href: userDashboardProfilePath.with({}),
          icon: 'person',
          linkType: 'Profile',
          label: i18n('Profile'),
        }
      : null,
    'professional-experience': hasResumeDb(user, isomorphicEnvironment, locale)
      ? {
          type: 'professional-experience',
          href: userDashboardProfessionalExperiencePath.with({}),
          icon: 'resume',
          linkType: 'Professional Experience',
          label: i18n('Professional Experience'),
        }
      : null,
    'notification-preferences': {
      type: 'notification-preferences',
      href: userDashboardNotificationPreferencesPath.with({}),
      icon: 'notification-preferences',
      linkType: 'Notification Preferences',
      label: i18n('Notification Preferences'),
    },
    'application-history': {
      type: 'application-history',
      href: userDashboardApplicationHistoryPath.with({}),
      icon: 'application-history',
      linkType: 'Application History',
      label: i18n('Application History'),
    },
    'account-settings': {
      type: 'account-settings',
      href: userDashboardAccountSettingsPath.with({}),
      icon: 'gear',
      linkType: 'Settings',
      label: i18n('Account Settings'),
    },
  };

  return order.flatMap((type) => rowsByType[type] ?? []);
}
